import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useCart } from "@/context/CartContext";
import { api, formatBRL, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { toast } from "sonner";
import { MapPin, ShoppingBag, CreditCard, QrCode, X } from "lucide-react";
import { formatPhoneBR, isValidPhoneBR, formatCPF, isValidCPF, onlyDigits } from "@/lib/validators";

export default function Checkout() {
  const { items, subtotal, clear } = useCart();
  const nav = useNavigate();
  const [form, setForm] = useState({ name: "", email: "", phone: "", cpf: "", notes: "" });
  const [errors, setErrors] = useState({});
  const [payment, setPayment] = useState("pix");
  const [couponInput, setCouponInput] = useState("");
  const [appliedCoupon, setAppliedCoupon] = useState(null); // {code, type, value}
  const [discount, setDiscount] = useState(0);
  const [loading, setLoading] = useState(false);

  const total = Math.max(0, subtotal - discount);
  const set = (k, v) => { setForm((f) => ({ ...f, [k]: v })); setErrors((e) => ({ ...e, [k]: undefined })); };

  const applyCoupon = async () => {
    const code = couponInput.trim().toUpperCase();
    if (!code) return;
    if (appliedCoupon && appliedCoupon.code === code) {
      toast.info(`Cupom ${code} já está aplicado`);
      return;
    }
    try {
      const { data } = await api.post(`/coupons/validate?code=${encodeURIComponent(code)}`);
      const d = data.type === "percent" ? subtotal * (data.value / 100) : data.value;
      setDiscount(Math.min(d, subtotal));
      setAppliedCoupon({ code: data.code, type: data.type, value: data.value });
      toast.success(`Cupom ${data.code} aplicado`);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const removeCoupon = () => {
    setAppliedCoupon(null);
    setDiscount(0);
    setCouponInput("");
    toast.info("Cupom removido");
  };

  const validate = () => {
    const errs = {};
    if (!form.name.trim()) errs.name = "Informe o nome completo";
    if (!form.email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) errs.email = "E-mail inválido";
    if (!isValidPhoneBR(form.phone)) errs.phone = "Telefone inválido. Use DDD + número (10 ou 11 dígitos).";
    if (form.cpf.trim() && !isValidCPF(form.cpf)) errs.cpf = "CPF inválido";
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const submit = async (e) => {
    e.preventDefault();
    if (items.length === 0) return;
    if (!validate()) return toast.error("Corrija os campos destacados");
    setLoading(true);
    try {
      const payload = {
        customer_name: form.name.trim(),
        customer_email: form.email.trim(),
        customer_phone: onlyDigits(form.phone),
        customer_cpf: form.cpf.trim() ? onlyDigits(form.cpf) : null,
        items: items.map((i) => ({ product_id: i.product_id, quantity: i.quantity, size: i.size, color: i.color })),
        payment_method: payment,
        coupon_code: appliedCoupon?.code || null,
        notes: form.notes,
      };
      const { data } = await api.post("/orders", payload);
      clear();
      nav(`/pedido/${data.id}`);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  if (items.length === 0) {
    return (
      <div className="max-w-lg mx-auto px-4 py-20 text-center space-y-4">
        <ShoppingBag className="h-16 w-16 mx-auto text-neutral-300" />
        <p className="text-neutral-600">Seu carrinho está vazio.</p>
        <Link to="/"><Button className="bg-[#C91F1F] hover:bg-[#9F1515]">Ver produtos</Button></Link>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto px-4 py-6 sm:py-10">
      <h1 className="text-2xl sm:text-3xl font-bold mb-6" style={{ fontFamily: "Outfit" }}>Finalizar pedido</h1>
      <form onSubmit={submit} className="grid lg:grid-cols-[1fr_380px] gap-8" noValidate>
        <div className="space-y-8">
          <section className="bg-white border rounded-xl p-5 space-y-4">
            <h2 className="font-bold">Identificação</h2>
            <div className="grid sm:grid-cols-2 gap-3">
              <div>
                <Label>Nome completo *</Label>
                <Input data-testid="checkout-name" value={form.name} onChange={(e) => set("name", e.target.value)} aria-invalid={!!errors.name} />
                {errors.name && <p className="text-xs text-red-600 mt-1" data-testid="err-name">{errors.name}</p>}
              </div>
              <div>
                <Label>E-mail *</Label>
                <Input data-testid="checkout-email" type="email" value={form.email} onChange={(e) => set("email", e.target.value)} aria-invalid={!!errors.email} />
                {errors.email && <p className="text-xs text-red-600 mt-1" data-testid="err-email">{errors.email}</p>}
              </div>
              <div>
                <Label>Telefone / WhatsApp *</Label>
                <Input
                  data-testid="checkout-phone"
                  value={form.phone}
                  onChange={(e) => set("phone", formatPhoneBR(e.target.value))}
                  placeholder="(21) 99999-9999"
                  inputMode="numeric"
                  aria-invalid={!!errors.phone}
                />
                {errors.phone && <p className="text-xs text-red-600 mt-1" data-testid="err-phone">{errors.phone}</p>}
              </div>
              <div>
                <Label>CPF (opcional)</Label>
                <Input
                  data-testid="checkout-cpf"
                  value={form.cpf}
                  onChange={(e) => set("cpf", formatCPF(e.target.value))}
                  placeholder="000.000.000-00"
                  inputMode="numeric"
                  aria-invalid={!!errors.cpf}
                />
                {errors.cpf && <p className="text-xs text-red-600 mt-1" data-testid="err-cpf">{errors.cpf}</p>}
              </div>
            </div>
          </section>

          <section className="bg-white border rounded-xl p-5 space-y-3">
            <div className="flex items-center gap-2 text-[#C91F1F]"><MapPin className="h-5 w-5" /><h2 className="font-bold text-neutral-900">Retirada na loja</h2></div>
            <p className="text-sm text-neutral-600">Rua Dr. Siqueira, nº 269, Magé - RJ</p>
            <p className="text-xs text-neutral-500">Aguarde a confirmação de que o pedido está pronto antes de vir buscar.</p>
          </section>

          <section className="bg-white border rounded-xl p-5 space-y-4">
            <h2 className="font-bold">Pagamento</h2>
            <RadioGroup value={payment} onValueChange={setPayment} className="space-y-2">
              <label className={`flex items-center gap-3 border rounded-lg p-3 cursor-pointer ${payment === "pix" ? "border-[#C91F1F] bg-red-50/40" : ""}`}>
                <RadioGroupItem value="pix" id="pix" data-testid="payment-pix" />
                <QrCode className="h-5 w-5" />
                <div className="flex-1"><span className="font-semibold">Pix</span><p className="text-xs text-neutral-500">Aprovação imediata • QR Code + Copia e Cola</p></div>
              </label>
              <label className={`flex items-center gap-3 border rounded-lg p-3 cursor-pointer ${payment === "card" ? "border-[#C91F1F] bg-red-50/40" : ""}`}>
                <RadioGroupItem value="card" id="card" data-testid="payment-card" />
                <CreditCard className="h-5 w-5" />
                <div className="flex-1"><span className="font-semibold">Cartão de crédito</span><p className="text-xs text-neutral-500">Em até 3x sem juros</p></div>
              </label>
            </RadioGroup>
            <div className="bg-amber-50 border border-amber-200 rounded-md p-3 text-xs text-amber-900">
              <strong>Ambiente de demonstração:</strong> pagamentos são processados em modo MOCK. Ative o Mercado Pago real no admin quando as credenciais estiverem disponíveis.
            </div>
          </section>

          <section className="bg-white border rounded-xl p-5 space-y-3">
            <h2 className="font-bold">Observações (opcional)</h2>
            <textarea value={form.notes} onChange={(e) => set("notes", e.target.value)} rows={3} className="w-full border rounded-md p-3 text-sm" placeholder="Alguma informação adicional?" />
          </section>
        </div>

        <aside className="bg-white border rounded-xl p-5 h-fit lg:sticky lg:top-24 space-y-4">
          <h2 className="font-bold">Resumo do pedido</h2>
          <div className="space-y-3 max-h-64 overflow-y-auto">
            {items.map((i) => (
              <div key={i.key} className="flex gap-3 text-sm">
                <img src={i.image} className="w-14 h-16 object-cover rounded" alt="" />
                <div className="flex-1">
                  <p className="font-medium line-clamp-1">{i.name}</p>
                  <p className="text-xs text-neutral-500">{[i.size, i.color].filter(Boolean).join(" • ")} • Qtd {i.quantity}</p>
                </div>
                <span className="font-semibold">{formatBRL(i.price * i.quantity)}</span>
              </div>
            ))}
          </div>

          <div className="border-t pt-3 space-y-2">
            {appliedCoupon ? (
              <div className="flex items-center justify-between bg-green-50 border border-green-200 rounded-md px-3 py-2 text-sm" data-testid="applied-coupon">
                <div>
                  <span className="font-semibold text-green-800">{appliedCoupon.code} aplicado</span>
                  <p className="text-xs text-green-700">{appliedCoupon.type === "percent" ? `${appliedCoupon.value}% de desconto` : `R$ ${appliedCoupon.value} de desconto`}</p>
                </div>
                <button type="button" onClick={removeCoupon} data-testid="remove-coupon-btn" className="text-green-800 hover:text-red-600 flex items-center gap-1 text-xs font-semibold">
                  <X className="h-3.5 w-3.5" /> Remover
                </button>
              </div>
            ) : (
              <>
                <div className="flex gap-2">
                  <Input value={couponInput} onChange={(e) => setCouponInput(e.target.value.toUpperCase())} placeholder="Cupom" data-testid="coupon-input" />
                  <Button type="button" variant="outline" onClick={applyCoupon} data-testid="coupon-apply">Aplicar</Button>
                </div>
              
              </>
            )}
          </div>

          <div className="border-t pt-3 space-y-1 text-sm">
            <div className="flex justify-between"><span>Subtotal</span><span>{formatBRL(subtotal)}</span></div>
            {discount > 0 && <div className="flex justify-between text-green-700"><span>Desconto</span><span>-{formatBRL(discount)}</span></div>}
            <div className="flex justify-between"><span>Retirada</span><span className="text-green-700">Grátis</span></div>
            <div className="flex justify-between text-lg font-bold pt-2 border-t"><span>Total</span><span className="text-[#C91F1F]">{formatBRL(total)}</span></div>
          </div>
          <Button data-testid="place-order-btn" type="submit" disabled={loading} className="w-full bg-[#C91F1F] hover:bg-[#9F1515] h-12 font-semibold">
            {loading ? "Processando..." : "Confirmar pedido"}
          </Button>
        </aside>
      </form>
    </div>
  );
}
